import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaSearch, FaFilter, FaTimes } from 'react-icons/fa';
import { useUser } from '../../contexts/UserContext';
import ProfileCard from './ProfileCard';
import ProfileView from './ProfileView';
import IdentityTags from './IdentityTags';
import { 
  genderIdentityOptions, 
  sexualOrientationOptions 
} from '../../utils/identityHelpers';
import '../../styles/ProfileList.css';

/**
 * Component for displaying a filterable list of user profiles
 */
const ProfileList = ({ 
  title = 'Community Members',
  showFilters = true,
  initialFilters = {},
  maxProfiles = null
}) => {
  const { currentUser, getFilteredUsers } = useUser();
  
  // State for filters
  const [filters, setFilters] = useState({
    destination: '',
    identityMarkers: [],
    onlyLocalQueers: false,
    ...initialFilters
  });
  
  // State for UI
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [profiles, setProfiles] = useState([]);
  
  // Load profiles based on filters
  useEffect(() => {
    const filteredProfiles = getFilteredUsers(filters);
    
    // Apply search query if present
    let searchedProfiles = filteredProfiles;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      searchedProfiles = filteredProfiles.filter(profile => 
        profile.name.toLowerCase().includes(query) || 
        profile.location.toLowerCase().includes(query) ||
        (profile.bio && profile.bio.toLowerCase().includes(query))
      );
    }
    
    // Exclude current user from the list
    const profilesWithoutCurrentUser = searchedProfiles.filter(
      profile => !currentUser || profile.id !== currentUser.id
    );
    
    // Limit the number of profiles if maxProfiles is set
    const limitedProfiles = maxProfiles 
      ? profilesWithoutCurrentUser.slice(0, maxProfiles)
      : profilesWithoutCurrentUser;
    
    setProfiles(limitedProfiles);
  }, [filters, searchQuery, currentUser, getFilteredUsers, maxProfiles]);
  
  // Handle search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  // Handle filter changes
  const handleDestinationChange = (e) => {
    setFilters(prev => ({ ...prev, destination: e.target.value }));
  };
  
  const handleIdentityMarkerToggle = (marker) => {
    setFilters(prev => {
      const currentMarkers = prev.identityMarkers || [];
      
      if (currentMarkers.includes(marker)) {
        return {
          ...prev,
          identityMarkers: currentMarkers.filter(m => m !== marker)
        };
      } else {
        return {
          ...prev,
          identityMarkers: [...currentMarkers, marker]
        };
      }
    });
  };
  
  const handleLocalQueersToggle = () => {
    setFilters(prev => ({ 
      ...prev, 
      onlyLocalQueers: !prev.onlyLocalQueers 
    }));
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      destination: '',
      identityMarkers: [],
      onlyLocalQueers: false
    });
    setSearchQuery('');
  };
  
  // Handle profile actions
  const handleViewProfile = (userId) => {
    setSelectedUserId(userId);
  };
  
  const handleCloseProfile = () => {
    setSelectedUserId(null);
  };
  
  const handleConnect = (userId) => {
    // In a real app, this would send a connection request
    console.log(`Connect with user ${userId}`);
  };
  
  const handleMessage = (userId) => {
    // In a real app, this would open a message thread
    console.log(`Message user ${userId}`);
  };
  
  // Render filter panel
  const renderFilterPanel = () => {
    if (!showFilters || !showFilterPanel) return null;
    
    return (
      <div className="profile-list__filter-panel">
        <h3 className="profile-list__filter-title">Filter Profiles</h3>
        
        <div className="profile-list__filter-section">
          <label className="profile-list__filter-label">Destination</label>
          <input
            type="text"
            className="profile-list__filter-input"
            placeholder="Filter by destination"
            value={filters.destination}
            onChange={handleDestinationChange}
          />
        </div>
        
        <div className="profile-list__filter-section">
          <label className="profile-list__filter-label">Identity</label>
          <div className="profile-list__filter-tags">
            <h4 className="profile-list__filter-subtitle">Gender Identity</h4>
            <IdentityTags
              tags={genderIdentityOptions}
              type="gender"
              size="small"
              selectable={true}
              selected={filters.identityMarkers || []}
              onClick={handleIdentityMarkerToggle}
            />
            
            <h4 className="profile-list__filter-subtitle">Sexual Orientation</h4>
            <IdentityTags
              tags={sexualOrientationOptions}
              type="orientation"
              size="small"
              selectable={true}
              selected={filters.identityMarkers || []}
              onClick={handleIdentityMarkerToggle}
            />
          </div>
        </div>
        
        <div className="profile-list__filter-section">
          <div className="profile-list__filter-checkbox">
            <input
              type="checkbox"
              id="localQueers"
              checked={filters.onlyLocalQueers}
              onChange={handleLocalQueersToggle}
            />
            <label htmlFor="localQueers">Only show local queer residents</label>
          </div>
        </div>
        
        <button 
          className="profile-list__filter-clear"
          onClick={clearFilters}
        >
          Clear all filters
        </button>
      </div>
    );
  };
  
  // If a profile is selected, show the profile view
  if (selectedUserId) {
    return (
      <ProfileView 
        userId={selectedUserId} 
        onClose={handleCloseProfile} 
      />
    );
  }
  
  return (
    <div className="profile-list">
      <div className="profile-list__header">
        <h2 className="profile-list__title">{title}</h2>
        
        {showFilters && (
          <div className="profile-list__filter-toggle">
            <button 
              className={`profile-list__filter-btn ${showFilterPanel ? 'profile-list__filter-btn--active' : ''}`}
              onClick={() => setShowFilterPanel(prev => !prev)}
              aria-label="Toggle filters"
            >
              <FaFilter />
              <span>Filters</span>
            </button>
          </div>
        )}
      </div>
      
      <div className="profile-list__search">
        <div className="profile-list__search-input-container">
          <FaSearch className="profile-list__search-icon" />
          <input
            type="text"
            className="profile-list__search-input"
            placeholder="Search members by name, location, or bio"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <button 
              className="profile-list__search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>
      
      <div className="profile-list__container">
        {showFilterPanel && renderFilterPanel()}
        
        <div className="profile-list__results">
          {profiles.length > 0 ? (
            <>
              <div className="profile-list__count">
                {profiles.length} {profiles.length === 1 ? 'member' : 'members'} found
              </div>
              
              <div className="profile-list__grid">
                {profiles.map(profile => (
                  <ProfileCard
                    key={profile.id}
                    user={profile}
                    variant="standard"
                    onConnect={handleConnect}
                    onMessage={handleMessage}
                    onViewProfile={handleViewProfile}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="profile-list__empty">
              <p>No members found matching your criteria.</p>
              {(filters.destination || filters.identityMarkers.length > 0 || filters.onlyLocalQueers || searchQuery) && (
                <button 
                  className="profile-list__empty-btn"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ProfileList.propTypes = {
  title: PropTypes.string,
  showFilters: PropTypes.bool,
  initialFilters: PropTypes.object,
  maxProfiles: PropTypes.number
};

export default ProfileList;
